import { Sun, Moon, User, LogOut, WalletCards } from "lucide-react"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/src/components/ui/dropdown-menu"
import { CommunityAvatar } from "@/src/components/community/community-avatar"
import { getDisplayName } from "@/lib/avatar"

interface UserMenuProps {
  avatarUrl?: string
  username?: string
  onGoToProfile: () => void
  onGoToOrders: () => void
  onToggleTheme: () => void
  onLogout: () => void
  isDark: boolean
  mounted: boolean
}

/**
 * UserMenu — 导航栏用户头像下拉菜单
 *
 * 显示用户头像（服务端 URL 或基于用户名自动生成），
 * 下拉包含个人中心、主题切换、登出。
 */
export function UserMenu({
  avatarUrl,
  username,
  onGoToProfile,
  onGoToOrders,
  onToggleTheme,
  onLogout,
  isDark,
  mounted,
}: UserMenuProps) {
  const displayName = getDisplayName({ username })

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="flex h-8 w-8 items-center justify-center rounded-full shrink-0 outline-none focus-visible:ring-2 focus-visible:ring-ring/50 transition-shadow duration-300 hover:shadow-[0_0_14px_4px_oklch(0.75_0.05_90/0.35)]"
          aria-label="用户菜单"
        >
          <CommunityAvatar src={avatarUrl} name={displayName} sizeClass="w-6 h-6" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        side="bottom"
        align="end"
        sideOffset={14}
        alignOffset={0}
        collisionPadding={16}
        className="liquid-glass-panel rounded-xl border-0 p-1.5 z-[51]"
      >
        <DropdownMenuItem
          onSelect={onGoToProfile}
          className="cursor-pointer flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-foreground/80 focus:bg-secondary/60"
        >
          <User className="h-4 w-4" />
          <span className="text-xs">个人中心</span>
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={onGoToOrders}
          className="cursor-pointer flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-foreground/80 focus:bg-secondary/60"
        >
          <WalletCards className="h-4 w-4" />
          <span className="text-xs">账户中心</span>
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={onToggleTheme}
          className="cursor-pointer flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-foreground/80 focus:bg-secondary/60"
        >
          {mounted && isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          <span className="text-xs">{mounted && isDark ? "浅色模式" : "深色模式"}</span>
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={onLogout}
          className="cursor-pointer flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-foreground/80 focus:bg-secondary/60"
        >
          <LogOut className="h-4 w-4" />
          <span className="text-xs">登出</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
